/**
 * Token Exchange page - handles cross-domain SSO token exchange
 *
 * When a user authenticates via SSO from a custom domain, the OAuth callback
 * goes to the main domain. The main domain creates a one-time code and redirects
 * here with that code. This page exchanges the code for actual tokens.
 */

import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '../../components/ui/alert';
import { Loader2 } from 'lucide-react';
import { authApi, AuthApiError } from '../../shared/auth/authApi';
import { AUTH_STORAGE_KEYS, EMULATION_STORAGE_KEYS } from '../../shared/auth/storageKeys';
import { setRefreshToken } from '../../shared/auth/secureTokenStore';
// Validates the `next` param as a same-origin path, so token-exchange can never
// be used as an open redirect.
import { sanitizeReturnPath } from '../../shared/auth/returnUrl';
import { clearPersistedQueryCache } from '../../shared/offline/queryPersister';
import { getGenericErrorMessage } from "@/shared/utils/errors";

export function TokenExchangePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation('auth');
  const { t: tCommon } = useTranslation('common');
  const queryClient = useQueryClient();

  const [status, setStatus] = useState<'loading' | 'error'>('loading');
  const [error, setError] = useState<string | null>(null);

  // Prevent duplicate code exchange (React StrictMode, i18n re-renders, etc.)
  const codeExchangeAttempted = useRef(false);

  useEffect(() => {
    const exchangeCode = async () => {
      // Prevent duplicate code exchange - one-time codes are single-use
      if (codeExchangeAttempted.current) {
        return;
      }

      const code = searchParams.get('code');
      const errorParam = searchParams.get('error');

      if (errorParam) {
        setError(t('sso.error'));
        setStatus('error');
        return;
      }

      if (!code) {
        setError('Missing authorization code');
        setStatus('error');
        return;
      }

      // Mark as attempted before the API call to prevent re-entry
      codeExchangeAttempted.current = true;

      try {
        // Exchange the one-time code for tokens
        const response = await authApi.exchangeCrossDomainCode(code);

        // Store tokens and redirect
        localStorage.setItem(AUTH_STORAGE_KEYS.accessToken, response.access_token);
        await setRefreshToken(response.refresh_token);
        localStorage.setItem(AUTH_STORAGE_KEYS.user, JSON.stringify(response.user));

        // If this hop carries an emulation session, restore the banner state so
        // ending emulation returns the sysadmin to their own session.
        if (response.emulation_session) {
          localStorage.setItem(
            EMULATION_STORAGE_KEYS.session,
            JSON.stringify(response.emulation_session),
          );
          localStorage.setItem(EMULATION_STORAGE_KEYS.token, response.access_token);
        }

        // This establishes a (possibly different) user's session on a browser
        // that may hold a prior user's persisted React Query cache. We hard-
        // reload below, after which PersistQueryClientProvider restores the
        // IndexedDB snapshot — so purge it FIRST (awaited) or the incoming user
        // would briefly render the previous user's cached data. Mirrors the
        // AuthContext / ExternalAuthContext auth-transition purge.
        await clearPersistedQueryCache(queryClient);

        // Redirect to `next` path (if provided and same-origin) or home, and
        // force reload to update auth state.
        const next = sanitizeReturnPath(searchParams.get('next'));
        window.location.href = next;
      } catch (err) {
        if (err instanceof AuthApiError) {
          // Handle specific error codes
          if (err.code === 'INVALID_CODE' || err.code === 'EXPIRED_CODE') {
            setError(t('sso.codeExpired', 'The authentication code has expired. Please try again.'));
          } else if (err.code === 'DOMAIN_MISMATCH') {
            setError(t('sso.domainMismatch', 'Security validation failed. Please try again.'));
          } else {
            setError(err.message);
          }
        } else {
          setError(getGenericErrorMessage());
        }
        setStatus('error');
      }
    };

    exchangeCode();
  }, [searchParams, t, tCommon, queryClient]);

  const handleRetry = () => {
    // Redirect to login page to start the SSO flow again
    navigate('/login');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="max-w-[400px] w-full">
        <CardContent className="p-8">
          {status === 'loading' && (
            <div className="text-center space-y-4">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
              <p className="text-muted-foreground">{t('sso.completing', 'Completing sign in...')}</p>
            </div>
          )}

          {status === 'error' && (
            <div className="space-y-6">
              <Alert variant="destructive">
                <AlertTitle>{tCommon('app.error')}</AlertTitle>
                <AlertDescription>{error}</AlertDescription>
              </Alert>
              <Button className="w-full" onClick={handleRetry}>
                {t('verification.goToLogin')}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
